import React, { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth, API } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import ReferralCard from "../components/ReferralCard";

const LEVELS = [
  { value: "diplomate", key: "level_diplomate" },
  { value: "standard", key: "level_standard" },
  { value: "passive_aggressive", key: "level_passive" },
  { value: "very_corporate", key: "level_very" },
];

const FORMATS = [
  { value: "email", key: "format_email" },
  { value: "teams", key: "format_teams" },
  { value: "slack", key: "format_slack" },
];

const LANGS = [
  { value: "fr", key: "lang_fr" },
  { value: "en", key: "lang_en" },
  { value: "nl", key: "lang_nl" },
];

export default function Translator() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [text, setText] = useState("");
  const [level, setLevel] = useState("standard");
  const [format, setFormat] = useState("email");
  const [outputLang, setOutputLang] = useState(lang);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [quota, setQuota] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [qrPlan, setQrPlan] = useState("");
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [founderInfo, setFounderInfo] = useState(null);
  const loadingTimer = useRef(null);

  const loadQuota = useCallback(async () => {
    try {
      const r = await fetch(`${API}/me/quota`, { credentials: "include" });
      if (r.ok) setQuota(await r.json());
    } catch (e) {
      // noop
    }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      const r = await fetch(`${API}/me/presets`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setPresets(data.presets || []);
      }
    } catch (e) {
      // noop
    }
  }, []);

  useEffect(() => {
    loadQuota();
    loadPresets();
    fetch(`${API}/stats/global`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setFounderInfo(d))
      .catch(() => {});
  }, [loadQuota, loadPresets]);

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error(t("presets_name_required"));
      return;
    }
    setSavingPreset(true);
    try {
      const r = await fetch(`${API}/me/presets`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: presetName.trim(),
          corporate_level: level,
          format,
          language: outputLang,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "error");
      }
      const preset = await r.json();
      setPresets((p) => [...p, preset]);
      setPresetName("");
      toast.success(t("presets_saved"));
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSavingPreset(false);
    }
  };

  const handleApplyPreset = (preset) => {
    setLevel(preset.corporate_level);
    setFormat(preset.format);
    setOutputLang(preset.language);
    toast.success(t("presets_applied"));
  };

  const handleDeletePreset = async (id) => {
    try {
      const r = await fetch(`${API}/me/presets/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (r.ok) {
        setPresets((p) => p.filter((x) => x.id !== id));
        toast.success(t("presets_deleted"));
      }
    } catch (e) {
      // noop
    }
  };

  const handleUpgrade = async (plan = "pro") => {
    setUpgrading(true);
    setQrPlan(plan);
    try {
      const r = await fetch(`${API}/checkout/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin, plan }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Stripe error");
      }
      const data = await r.json();
      setCheckoutUrl(data.url);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setUpgrading(false);
    }
  };

  const closeQr = () => {
    setCheckoutUrl("");
    setQrPlan("");
  };

  useEffect(() => {
    setOutputLang(lang);
  }, [lang]);

  const cycleLoadingMessage = () => {
    const messages = [t("loading_1"), t("loading_2"), t("loading_3"), t("loading_4")];
    let i = 0;
    setLoadingMsg(messages[0]);
    loadingTimer.current = setInterval(() => {
      i = (i + 1) % messages.length;
      setLoadingMsg(messages[i]);
    }, 900);
  };

  const stopLoading = () => {
    if (loadingTimer.current) clearInterval(loadingTimer.current);
    loadingTimer.current = null;
    setLoadingMsg("");
  };

  const handleTranslate = async () => {
    if (!text.trim()) {
      toast.error(t("err_empty"));
      return;
    }
    setOutput("");
    setLoading(true);
    cycleLoadingMessage();
    try {
      const r = await fetch(`${API}/translate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
          corporate_level: level,
          format,
          language: outputLang,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || t("err_generic"));
      }
      const data = await r.json();
      setOutput(data.translation);
      if (data.quota_remaining !== null && data.quota_remaining !== undefined) {
        setQuota((q) => (q ? { ...q, quota_remaining: data.quota_remaining } : q));
      }
    } catch (e) {
      const msg = e.message || t("err_generic");
      if (typeof msg === "string" && msg.includes("quota_exhausted")) {
        toast.error(t("err_quota"));
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      stopLoading();
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    await navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success(t("copied_toast"));
    setTimeout(() => setCopied(false), 1800);
  };

  // Founder discount applicable to the current user (already a founder, or a
  // prospective founder while seats remain). Drives the struck-through pricing.
  const founderRate =
    quota && quota.founder_discount > 0
      ? quota.founder_discount
      : founderInfo && founderInfo.founder_slots_remaining > 0
      ? founderInfo.founder_next_discount || 0
      : 0;

  const fmtEur = (v) =>
    `${v.toFixed(2).replace(".", lang === "en" ? "." : ",")} €`;

  const renderPlanPrice = (plan) => {
    if (!founderInfo) return null;
    const base = plan === "business" ? founderInfo.business_price_eur : founderInfo.pro_price_eur;
    if (!founderRate || !base) return null;
    const discounted = base * (1 - founderRate);
    return (
      <p
        data-testid={`founder-price-${plan}`}
        className="font-mono-corp text-xs flex flex-wrap items-center gap-2"
      >
        <span className="line-through text-[#A0A09C]">{fmtEur(base)}</span>
        <span className="text-[#FFCC00] font-bold text-sm">{fmtEur(discounted)}</span>
        <span className="bg-[#FFCC00] text-[#0A0A0A] font-bold px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
          -{Math.round(founderRate * 100)}% {t("founder_lifetime_tag")}
        </span>
      </p>
    );
  };

  return (
    <main className="flex-grow" data-testid="translator-app">
      {/* STRIPE QR MODAL */}
      {checkoutUrl && (
        <div
          data-testid="qr-modal-overlay"
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/70 flex items-center justify-center p-4"
          onClick={closeQr}
        >
          <div
            data-testid="qr-modal"
            className="bg-[#F4F4F0] border-2 border-[#0A0A0A] shadow-brutal max-w-sm w-full p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold mb-3">
              // {qrPlan === "business" ? t("plan_business_title") : t("plan_pro_title")}
            </p>
            <h2 className="font-display text-2xl uppercase tracking-tight text-[#0A0A0A] mb-2">
              {t("qr_title")}
            </h2>
            <p className="font-mono-corp text-xs text-[#4B4B4B] leading-relaxed mb-5">
              {t("qr_subtitle")}
            </p>
            <div className="flex justify-center mb-6">
              <div className="bg-white border-2 border-[#0A0A0A] p-4 shadow-brutal-sm">
                <QRCodeSVG
                  data-testid="qr-code-svg"
                  value={checkoutUrl}
                  size={200}
                  level="M"
                  fgColor="#0A0A0A"
                  bgColor="#FFFFFF"
                />
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <a
                data-testid="qr-pay-this-device"
                href={checkoutUrl}
                className="bg-[#FF3B30] text-white border-2 border-[#0A0A0A] font-display text-sm uppercase tracking-widest px-6 py-3 text-center shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
              >
                {t("qr_continue_device")} →
              </a>
              <button
                data-testid="qr-close-btn"
                onClick={closeQr}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] font-display text-sm uppercase tracking-widest px-6 py-3 hover:bg-[#FFCC00] transition-colors"
              >
                {t("qr_close")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {/* TOP STRIP */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-8">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold mb-2">
              // SESSION ACTIVE — {user?.email}
            </p>
            <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">
              CONSOLE DE TRADUCTION
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-[#4B4B4B]">
            {quota && quota.effective_plan === "business" ? (
              <span
                data-testid="quota-business-badge"
                className="bg-[#FFCC00] border-2 border-[#0A0A0A] px-2 py-1 font-bold text-[#0A0A0A]"
              >
                BUSINESS · {quota.quota_remaining}/{quota.quota_total}
              </span>
            ) : quota && quota.effective_plan === "pro" ? (
              <span
                data-testid="quota-pro-badge"
                className="bg-white border-2 border-[#0A0A0A] px-2 py-1 font-bold text-[#0A0A0A]"
              >
                {t("pro_badge")} · {quota.quota_remaining}/{quota.quota_total}
              </span>
            ) : quota ? (
              <span data-testid="quota-remaining" className="font-bold">
                {t("quota_remaining")}: {quota.quota_remaining}/{quota.quota_total}
              </span>
            ) : null}
            <span className="inline-block w-2 h-2 bg-[#FFCC00] border border-[#0A0A0A]" />
            STATUS: ONLINE
          </div>
        </div>

        {/* Founder member status (already a founder) */}
        {quota && quota.founder_discount > 0 && (
          <div
            data-testid="founder-member-banner"
            className="mb-4 bg-[#FFCC00] border-2 border-[#0A0A0A] shadow-brutal px-4 py-3 font-bold uppercase text-xs md:text-sm tracking-wide text-[#0A0A0A]"
          >
            ★ {t("founder_you_are", { pct: Math.round(quota.founder_discount * 100) })}
          </div>
        )}

        {/* Founder offer (seats remaining, user not yet founder) */}
        {quota && !quota.founder_discount && founderInfo && founderInfo.founder_slots_remaining > 0 && (
          <div
            data-testid="founder-offer-banner"
            className="mb-4 bg-[#FF3B30] text-[#F4F4F0] border-2 border-[#0A0A0A] shadow-brutal px-4 py-3 font-bold uppercase text-xs md:text-sm tracking-wide"
          >
            ★ {t("founder_offer", {
              remaining: founderInfo.founder_slots_remaining,
              pct: Math.round(founderInfo.founder_next_discount * 100),
            })}
          </div>
        )}

        {/* Upgrade banner: free → Pro + Business, pro → Business upsell, business → hidden */}
        {quota && quota.effective_plan !== "business" && (
          <div
            data-testid="upgrade-banner"
            className="mb-8 bg-[#0A0A0A] text-[#F4F4F0] border-2 border-[#0A0A0A] shadow-brutal p-5 grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {quota.effective_plan === "free" && (
              <div className="bg-[#1a1a1a] border-2 border-[#FFCC00] p-4 flex flex-col gap-2">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#FFCC00] font-bold">
                  // OPTION 01
                </p>
                <p className="font-display text-xl uppercase">{t("plan_pro_title")}</p>
                <p className="font-mono-corp text-xs text-[#A0A09C]">{t("plan_pro_sub")}</p>
                {renderPlanPrice("pro")}
                <button
                  data-testid="upgrade-pro-btn"
                  onClick={() => handleUpgrade("pro")}
                  disabled={upgrading}
                  className="mt-2 bg-[#FFCC00] text-[#0A0A0A] border-2 border-[#FFCC00] font-display text-sm uppercase tracking-widest px-4 py-2 hover:bg-white hover:border-white transition-all disabled:opacity-50"
                >
                  {t("plan_pick")} →
                </button>
              </div>
            )}
            <div className={`bg-[#1a1a1a] border-2 border-[#FF3B30] p-4 flex flex-col gap-2 ${quota.effective_plan === "pro" ? "md:col-span-2" : ""}`}>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold">
                // {quota.effective_plan === "pro" ? "PASSER BUSINESS" : "OPTION 02"}
              </p>
              <p className="font-display text-xl uppercase">{t("plan_business_title")}</p>
              <p className="font-mono-corp text-xs text-[#A0A09C]">{t("plan_business_sub")}</p>
              {renderPlanPrice("business")}
              <button
                data-testid="upgrade-business-btn"
                onClick={() => handleUpgrade("business")}
                disabled={upgrading}
                className="mt-2 bg-[#FF3B30] text-white border-2 border-[#FF3B30] font-display text-sm uppercase tracking-widest px-4 py-2 hover:bg-white hover:text-[#0A0A0A] hover:border-white transition-all disabled:opacity-50"
              >
                {t("plan_pick")} →
              </button>
            </div>
          </div>
        )}

        {/* GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* LEFT — RAW INPUT */}
          <section
            data-testid="raw-input-panel"
            className="bg-white border-2 border-[#0A0A0A] shadow-brutal p-6 flex flex-col gap-5"
          >
            <div>
              <h2 className="font-display text-xl uppercase border-b-2 border-[#0A0A0A] pb-2">
                [01] {t("panel_raw")}
              </h2>
              <p className="text-xs uppercase tracking-widest text-[#4B4B4B] mt-2">
                {t("panel_raw_hint")}
              </p>
            </div>

            <textarea
              data-testid="raw-input-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("placeholder_raw")}
              rows={10}
              className="bg-[#F4F4F0] border-2 border-[#0A0A0A] p-4 font-mono-corp text-base text-[#0A0A0A] focus:outline-none focus:bg-white focus:shadow-[inset_4px_4px_0px_0px_rgba(255,204,0,1)] resize-none w-full"
            />

            {/* Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-bold tracking-[0.2em] uppercase">
                  {t("label_level")}
                </label>
                <select
                  data-testid="select-level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="bg-white border-2 border-[#0A0A0A] px-3 py-2 font-mono-corp text-sm focus:outline-none focus:bg-[#FFCC00] cursor-pointer"
                >
                  {LEVELS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {t(l.key)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-bold tracking-[0.2em] uppercase">
                  {t("label_format")}
                </label>
                <select
                  data-testid="select-format"
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                  className="bg-white border-2 border-[#0A0A0A] px-3 py-2 font-mono-corp text-sm focus:outline-none focus:bg-[#FFCC00] cursor-pointer"
                >
                  {FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {t(f.key)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-bold tracking-[0.2em] uppercase">
                  {t("label_language")}
                </label>
                <select
                  data-testid="select-output-language"
                  value={outputLang}
                  onChange={(e) => setOutputLang(e.target.value)}
                  className="bg-white border-2 border-[#0A0A0A] px-3 py-2 font-mono-corp text-sm focus:outline-none focus:bg-[#FFCC00] cursor-pointer"
                >
                  {LANGS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {t(l.key)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              data-testid="translate-submit-btn"
              onClick={handleTranslate}
              disabled={loading}
              className="mt-2 bg-[#FF3B30] text-white border-2 border-[#0A0A0A] font-display text-base uppercase tracking-widest px-8 py-4 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-brutal-sm active:translate-x-[6px] active:translate-y-[6px] active:shadow-none transition-all disabled:bg-[#4B4B4B] disabled:cursor-not-allowed"
            >
              {loading ? loadingMsg || t("loading_1") : `${t("btn_translate")} →`}
            </button>

            {/* PRESETS */}
            <div data-testid="presets-section" className="border-t-2 border-[#0A0A0A] pt-4 mt-1">
              <h3 className="font-display text-sm uppercase tracking-widest">
                ★ {t("presets_title")}
              </h3>
              <p className="text-[10px] uppercase tracking-widest text-[#4B4B4B] mt-1 mb-3">
                {t("presets_hint")}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <input
                  data-testid="preset-name-input"
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder={t("presets_name_placeholder")}
                  maxLength={60}
                  className="flex-grow bg-[#F4F4F0] border-2 border-[#0A0A0A] px-3 py-2 font-mono-corp text-sm focus:outline-none focus:bg-white"
                />
                <button
                  data-testid="preset-save-btn"
                  onClick={handleSavePreset}
                  disabled={savingPreset}
                  className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] font-display text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#FFCC00] hover:text-[#0A0A0A] transition-all disabled:opacity-50 whitespace-nowrap"
                >
                  {t("presets_save")}
                </button>
              </div>
              {presets.length === 0 ? (
                <p
                  data-testid="presets-empty"
                  className="text-xs font-mono-corp text-[#4B4B4B]"
                >
                  {t("presets_empty")}
                </p>
              ) : (
                <ul data-testid="presets-list" className="flex flex-col gap-2">
                  {presets.map((p) => (
                    <li
                      key={p.id}
                      data-testid={`preset-item-${p.id}`}
                      className="flex items-center justify-between gap-2 bg-[#F4F4F0] border-2 border-[#0A0A0A] px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="font-display text-sm uppercase truncate">{p.name}</p>
                        <p className="text-[10px] font-mono-corp text-[#4B4B4B] uppercase truncate">
                          {t(LEVELS.find((l) => l.value === p.corporate_level)?.key || "")} ·{" "}
                          {t(FORMATS.find((f) => f.value === p.format)?.key || "")} ·{" "}
                          {t(LANGS.find((l) => l.value === p.language)?.key || "")}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          data-testid={`preset-apply-${p.id}`}
                          onClick={() => handleApplyPreset(p)}
                          className="bg-[#FFCC00] text-[#0A0A0A] border-2 border-[#0A0A0A] font-display text-[10px] uppercase tracking-widest px-2 py-1 hover:bg-[#0A0A0A] hover:text-white transition-all"
                        >
                          {t("presets_apply")}
                        </button>
                        <button
                          data-testid={`preset-delete-${p.id}`}
                          onClick={() => handleDeletePreset(p.id)}
                          className="bg-white text-[#FF3B30] border-2 border-[#0A0A0A] font-display text-[10px] uppercase tracking-widest px-2 py-1 hover:bg-[#FF3B30] hover:text-white transition-all"
                        >
                          ✕
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* RIGHT — CORPORATE OUTPUT */}
          <section
            data-testid="output-panel"
            className="bg-[#FFCC00] border-2 border-[#0A0A0A] shadow-brutal p-6 flex flex-col gap-5"
          >
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-display text-xl uppercase border-b-2 border-[#0A0A0A] pb-2">
                  [02] {t("panel_output")}
                </h2>
                <p className="text-xs uppercase tracking-widest text-[#0A0A0A] mt-2">
                  {FORMATS.find((f) => f.value === format)
                    ? t(FORMATS.find((f) => f.value === format).key)
                    : ""}{" "}
                  · {LEVELS.find((l) => l.value === level)
                    ? t(LEVELS.find((l) => l.value === level).key)
                    : ""}
                </p>
              </div>
              <button
                data-testid="copy-clipboard-btn"
                onClick={handleCopy}
                disabled={!output}
                className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] font-display text-xs uppercase tracking-widest px-4 py-2 shadow-brutal-sm hover:bg-[#0A0A0A] hover:text-white hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {copied ? t("btn_copied") : t("btn_copy")}
              </button>
            </div>

            <div
              data-testid="output-text"
              className="flex-grow bg-white border-2 border-[#0A0A0A] p-4 font-mono-corp text-base text-[#0A0A0A] min-h-[280px] whitespace-pre-wrap leading-relaxed"
            >
              {loading ? (
                <span className="text-[#4B4B4B]">
                  <span className="blink">▮</span> {loadingMsg}
                </span>
              ) : output ? (
                output
              ) : (
                <span className="text-[#4B4B4B]">{t("panel_output_empty")}</span>
              )}
            </div>

            <p className="text-[10px] tracking-[0.3em] uppercase text-[#0A0A0A] font-bold">
              ▸ READY FOR DISTRIBUTION · COMPLIANCE: PASSED
            </p>
          </section>
        </div>

        <ReferralCard />
      </div>
    </main>
  );
}
